import {
  CONTEXT_KIND_HEADING,
  CONTEXT_KIND_ORDER,
  type ContextKind,
  isContextKind,
} from "@pilotfish/core";
import { useCallback, useEffect, useState } from "react";
import { type AppApi, AppRequestError } from "../api/client";
import {
  confirmWrite,
  type FactSubject,
  type ForgetEnvelope,
  fetchMemoryPage,
  type MemoryFact,
  newIdempotencyKey,
  proposeCorrection,
  proposeForget,
  proposeRemember,
  type WriteEnvelope,
} from "../api/memory";
import { fetchRoster, type RosterMember } from "../api/roster";
import { Confirm } from "../components/Confirm";
import { FactForm } from "../components/FactForm";
import { DisclosureIcon, PlusIcon } from "../components/icons";
import { Screen } from "../components/Screen";
import { ErrorState, Loading } from "../components/states";
import {
  EVERYONE,
  type Whose,
  WhoseFilter,
  whoseAdmits,
  whoseMatches,
  whoseOptions,
} from "../components/WhoseFilter";
import { usePanel } from "../panelContext";
import { spell } from "../words";

/**
 * **What Boone knows** — the memory screen (issues #302 and #329, ADR 0021 decision 5).
 *
 * The reason the control layer exists. `CLAUDE.md`'s trust rule is that saved family memory is
 * *visible, editable and deletable*, and until this screen all three meant asking an assistant
 * to read the record out and hoping a paraphrase reached the right tool.
 *
 * ## Nothing here is a second way to write
 *
 * Correcting a fact is `remember`. Deleting one is `forget_this`. Not something like them —
 * the same plain functions, reached through #298's routes, with the same tier, the same
 * `PROPOSAL#`, the same `ACTION#` and the same #114 scrub. This component decides **when to
 * ask the server** and **what to render**, and decides nothing about what a write means.
 *
 * That is why it never predicts a tier. A write is sent; the answer says `applied` or
 * `proposed`; a proposal opens the confirmation. A screen that knew which writes would ask
 * would be a second copy of `confirmation.ts` living in a browser, and it would be wrong the
 * first time a collision made a low-risk write into a replacement.
 *
 * ## All five sections, always — which is where this diverges from `show_what_you_know`
 *
 * That tool omits empty kinds on purpose: *"an empty section is noise, not information"*. On a
 * screen a section is also **the way in** — the add control lives inside it, and the kind is
 * taken from the heading somebody pressed it under — so hiding an empty one would hide the
 * only way to record the family's first goal. The headings and their order are
 * `@pilotfish/core`'s, shared with that tool so the two surfaces cannot name the same box
 * differently.
 *
 * ## The screen is the whole record, and that is what lets it say so (#329)
 *
 * `Memory.dc.html` opens *"Everything I've saved about your family — sixteen things in all.
 * That's the lot; nothing is held back"*, puts a count on every section heading, and truncates
 * a long section behind *Show all 12* with *"nothing is held back; this section is just
 * long"*. Every one of those is a claim about **completeness**, and none of them can be made
 * from one page of a paged read.
 *
 * So the load walks the cursor to the end rather than fetching page one and offering a *Show
 * more* (#302's shape). What replaces the global pager is per-section folding, which is
 * presentation over a record that is already all here. Two consequences are worth knowing:
 *
 * - **Every completeness claim is conditional on {@link Load.truncated}.** The walk stops at
 *   {@link MAX_ITEMS}, and past that the blurb drops its claim, the counts disappear, and the
 *   screen says what it is showing and where the rest is. A count that might be wrong is the
 *   #265 class of bug — a number a family reads and believes — so there is one rule and it is
 *   applied to the blurb, the section headings and the fold alike.
 * - **A refresh re-reads everything too**, which made it much simpler than #302's: it no
 *   longer has to walk *"until it has seen the refs that are on screen"*, because on screen
 *   and in the record are now the same thing.
 *
 * ## A kind this build does not know is shown, not swallowed
 *
 * `kind` arrives as a plain string. If a sixth is ever added server-side, an older bundle has
 * no heading for it — and dropping those records would make this screen quietly incomplete,
 * which is the "inventory lies" failure the export's own design refuses. So they are grouped
 * under their raw kind, with no add control, and remain readable and deletable.
 */
export function Memory({ familyId }: { familyId: string }) {
  const { api } = usePanel();

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const [form, setForm] = useState<FormState | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [whose, setWhose] = useState<Whose>("all");
  /** Sections the family has folded away, and sections they have asked to see in full. */
  const [folded, setFolded] = useState<ReadonlySet<string>>(EMPTY);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(EMPTY);
  /**
   * The control to hand focus back to once a form closes.
   *
   * A key rather than a node, because the trigger is *conditionally rendered*: opening the form
   * unmounts it and cancelling mounts a **new** element, so a captured reference would point at
   * a node that is no longer in the document. The effect below finds the fresh one.
   */
  const [restoreFocusTo, setRestoreFocusTo] = useState<string | null>(null);

  const closeForm = useCallback((focusKey: string | null) => {
    setForm(null);
    setRestoreFocusTo(focusKey);
  }, []);

  useEffect(() => {
    if (form !== null || restoreFocusTo === null) return;
    const target = document.querySelector<HTMLElement>(`[data-focus-key="${restoreFocusTo}"]`);
    target?.focus();
    setRestoreFocusTo(null);
  }, [form, restoreFocusTo]);

  // `attempt` is not read in the effect — it IS the trigger, the shell's own idiom (Panel.tsx).
  // Every write that applies calls `reload()`, and the inputs it would otherwise depend on are
  // unchanged by definition: the same family, the same api. Removing it, as the rule's fix
  // suggests, makes the screen never re-read after a correction.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the re-run trigger.
  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    Promise.all([readWholeRecord(api, familyId), fetchRoster(api, familyId)])
      .then(([record, roster]) => {
        if (!live) return;
        setLoad({
          status: "ready",
          facts: record.facts,
          truncated: record.truncated,
          members: roster.members,
        });
      })
      .catch((error: unknown) => {
        if (live) setLoad({ status: "failed", detail: detailOf(error) });
      });
    return () => {
      live = false;
    };
  }, [api, familyId, attempt]);

  /**
   * Re-read the record **without taking the screen down**.
   *
   * `reload()` sets `status: "loading"`, which swaps the whole tree for a spinner — and an open
   * form goes with it, taking whatever somebody had typed. That is fine after an applied write
   * (the form is deliberately closed anyway) and destructive anywhere else: a transport failure
   * while writing wiped the draft, on the one path where the write may also have landed.
   *
   * **A `FactRow` and the `FactForm` inside it survive this only while `fact.ref` is still in
   * the list** — that is what keeps the element mounted and its state intact. #302's version
   * had to walk the cursor far enough to bring back every ref that was on screen, and track
   * which ones it had seen, because a refresh of page one alone silently dropped everything
   * loaded with *Show more* and unmounted any row mid-edit. Reading the whole record makes that
   * bookkeeping unnecessary rather than careful, which is the better fix: there is no count to
   * get wrong (#302's first attempt kept one in a ref and mutated it inside a `setLoad`
   * updater, a side effect in a function React may call twice, discard or replay).
   *
   * **It reports whether it worked** (PR #336 review, finding 4 — raised against the list
   * screen, and true here word for word). {@link AMBIGUOUS_WRITE_FAILURE} tells a family *"your
   * record has been re-read below"*, and during the outage that loses a write's response these
   * two GETs fail too — so the claim was made about a screen that had not been refreshed, to
   * somebody deciding whether to retry an irreversible delete.
   *
   * It still throws nothing: a failed background read is a fact the caller folds into the one
   * sentence it is already writing, not a second contradicting message.
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const [record, roster] = await Promise.all([
        readWholeRecord(api, familyId),
        fetchRoster(api, familyId),
      ]);
      setLoad((current) =>
        current.status === "ready"
          ? {
              ...current,
              facts: record.facts,
              truncated: record.truncated,
              members: roster.members,
            }
          : current,
      );
      return true;
    } catch {
      return false;
    }
  }, [api, familyId]);

  /**
   * Send a write, and route its answer.
   *
   * The whole of this component's write logic, shared by adding, correcting and deleting,
   * because the branch is the *envelope's* and not the operation's: `proposed` opens the
   * confirmation, `applied` closes the form and re-reads. A refusal shows the server's own
   * sentence where it gave one — a spent allowance and an owners-only tool are answers, not
   * faults, and inventing our own account of one is what ADR 0021 decision 5 refuses.
   */
  const send = useCallback(
    async <T extends WriteEnvelope>(
      run: () => Promise<T>,
      tail: "/remember" | "/correct-this" | "/forget-this",
      at: RefusalAt,
      /**
       * Show the whole record once this lands — see {@link saveFact}.
       *
       * Carried through the proposal rather than acted on at submit, because a family who
       * cancels the confirmation has not saved anything and should be looking at exactly what
       * they were looking at before.
       */
      reveal = false,
    ): Promise<void> => {
      setPending(true);
      setRefusal(null);
      try {
        const envelope = await run();
        if (envelope.status === "proposed") {
          // A proposal with no id cannot be confirmed, and falling through to the branch below
          // would close the form, clear the dialog and re-read — which is this screen's entire
          // vocabulary for "that worked", said about a write that did not happen. #298 returns
          // an id with every proposal, so this is unreachable; it is handled because the
          // failure mode is the one thing this screen may never do.
          if (envelope.proposalId === null) {
            setRefusal({ detail: GENERIC_WRITE_FAILURE, at });
            return;
          }
          setConfirming({ envelope, tail, proposalId: envelope.proposalId, reveal });
          return;
        }
        setForm(null);
        setConfirming(null);
        // **The write landed somewhere the filter is hiding** (PR #330 review). A family
        // filtered to *Dana's* who adds a family goal would otherwise watch the list re-read
        // without their new fact in it, with the blurb's count the only sign it worked.
        if (reveal) setWhose(EVERYONE);
        reload();
      } catch (error: unknown) {
        const detail = detailOf(error);
        if (detail !== null) {
          setRefusal({ detail, at });
        } else if (wasAnswered(error)) {
          // The server answered and refused, so nothing was written and saying so is true.
          setRefusal({ detail: GENERIC_WRITE_FAILURE, at });
        } else {
          // **Only when we genuinely do not know.** An unanswered write may have committed, so
          // the screen should show what is actually stored rather than leave a family to guess
          // from a sentence — through {@link refresh}, which does not take the form and its
          // draft down with it, and which now says whether it managed to.
          //
          // The sentence goes up **at once** and claims nothing about that read: during the
          // outage this describes the re-read can hang for as long as the browser's own
          // timeout, and a family left with no message at all is worse off than one told
          // immediately. The second sentence replaces it only if the read actually fails.
          setRefusal({ detail: AMBIGUOUS_WRITE_FAILURE, at });
          void refresh().then((reconciled) => {
            if (reconciled) return;
            // **Only if it is still the sentence we wrote.** A newer write clears the refusal
            // and may set its own, and a late answer about an abandoned one would overwrite it.
            setRefusal((current) =>
              current !== null && current.detail === AMBIGUOUS_WRITE_FAILURE
                ? { detail: AMBIGUOUS_AND_UNREAD, at: current.at }
                : current,
            );
          });
        }
      } finally {
        setPending(false);
      }
    },
    [reload, refresh],
  );

  /**
   * Saving the form: **adding a fact and correcting one are different writes** (issues #83,
   * #306).
   *
   * A new fact is a `remember` — it has no identity yet, and the title is how it will be
   * stored. An existing one is a `correct_this`, addressed by the fact rather than by its
   * title, so a changed name moves the row instead of writing a second fact beside it.
   *
   * That is what lets this be **one form and one confirmation** whatever the family edited.
   * It shipped briefly as an Edit form plus a separate Rename button, which made them pay for
   * the fact that a title happens to be the sort key.
   *
   * A correction sends **only what changed**, because absent means unchanged — which also
   * retires `carriedOver`: `remember` rewrites the whole record and would drop `rationale`,
   * `importance` and `expiresAt` unless the panel handed them back, and this touches nothing
   * it was not asked to.
   */
  const saveFact = (
    kind: ContextKind,
    values: { title: string; content: string; subject: FactSubject; existing?: MemoryFact },
  ) => {
    const existing = values.existing;
    if (existing !== undefined) {
      const patch = {
        ...(values.title !== existing.title ? { newTitle: values.title } : {}),
        ...(values.content !== existing.content ? { content: values.content } : {}),
        // **Always sent, where the other two are sent only when they moved.** The panel's read
        // gives a member subject a *name* and no id (`DisclosedContext`), so it cannot tell two
        // same-named members apart — and comparing labels would be the bug PR #276 found one
        // layer down. The form always holds a stated subject, so sending it costs nothing and
        // the server decides whether it is a change: `findContextToCorrect` compares the
        // corrected fact field by field and refuses one that changes nothing.
        subject: values.subject,
      };
      return void send(
        () =>
          proposeCorrection(api, familyId, {
            contextType: existing.kind,
            title: existing.title,
            ...patch,
            idempotencyKey: newIdempotencyKey(),
          }),
        "/correct-this",
        "form",
        !whoseAdmits(values.subject, whose),
      );
    }
    return void send(
      () =>
        proposeRemember(api, familyId, {
          contextType: kind,
          title: values.title,
          content: values.content,
          subject: values.subject,
          // The words they typed, which is what makes `directed` honestly theirs (#302).
          rawText: values.content,
          idempotencyKey: newIdempotencyKey(),
        }),
      "/remember",
      "form",
      // **Whether this fact will land where the family can see it.** The form starts on
      // *Choose…* whatever the chip says (#92 refuses a default subject), so somebody filtered
      // to *Dana's* can perfectly reasonably save something as the family's — and then watch
      // the list re-read without it. Nothing about the write is wrong; the view is.
      !whoseAdmits(values.subject, whose),
    );
  };

  const deleteFact = (fact: MemoryFact) =>
    void send(
      () =>
        proposeForget(api, familyId, {
          contextType: fact.kind,
          title: fact.title,
          idempotencyKey: newIdempotencyKey(),
        }),
      "/forget-this",
      "page",
    );

  const confirm = () => {
    if (confirming === null) return;
    const { tail, proposalId, reveal } = confirming;
    void send(() => confirmWrite(api, familyId, tail, proposalId), tail, "dialog", reveal);
  };

  if (load.status === "loading") {
    return (
      <Screen title={TITLE} blurb={OPENING}>
        <Loading label="Reading what Boone keeps…" />
      </Screen>
    );
  }

  if (load.status === "failed") {
    return (
      <Screen title={TITLE} blurb={OPENING}>
        <ErrorState
          title="We couldn't read your family's record"
          body={load.detail ?? GENERIC_READ_FAILURE}
        />
      </Screen>
    );
  }

  const groups = groupByKind(load.facts);
  // Read once per render so every row on the page is judged against the same instant.
  const now = Date.now();
  // A proposal is open: every control that could start a *second* write is out of bounds until
  // it is answered. Without this, the page behind the scrim stays live — which is how Shift+Tab
  // reached the delete control and swapped the proposal under the dialog (Confirm.tsx).
  const locked = pending || confirming !== null;
  // Whether the record on screen is all of it — the condition on every count and every
  // completeness claim below. See the component header.
  const complete = !load.truncated;
  const matching = load.facts.filter((fact) => whoseMatches(fact, whose));
  const filtered = whose !== "all";

  const toggle = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (!next.delete(key)) next.add(key);
    return next;
  };

  return (
    <Screen
      title={TITLE}
      blurb={opening(load.facts.length, complete)}
      aside={
        <WhoseFilter
          value={whose}
          options={whoseOptions(load.members, load.facts)}
          /*
            **The chips wait for an open form.** A filter change unmounts every row it excludes
            — React reconciles them by `fact.ref` — and one of those rows may hold a half-typed
            correction. The fold is answered by keeping the section open; this one cannot be,
            because pinning the edited fact into a filtered list would make the count beside the
            heading disagree with the rows under it. Finishing or cancelling costs one click.
          */
          disabled={form !== null || locked}
          onChange={setWhose}
        />
      }
    >
      {/*
        A refusal that belongs to the page rather than to an open form or the dialog.
        Deleting is the case: its propose is the first call, so a refusal arrives with no form
        and no dialog to put it in. `at` is stated by whoever sent the write, so a delete
        refused while an unrelated add form happens to be open no longer renders inside it.
      */}
      {refusal !== null && refusal.at === "page" ? (
        <div className="memory__refusal" role="alert">
          <p>{refusal.detail}</p>
        </div>
      ) : null}

      {/*
        **The record is genuinely empty**, which is different from a section being empty and is
        worth its own words: `MemoryStates.dc.html` draws it as a whole-screen state saying so
        outright. The five headings still render underneath with their own *Add*, because on
        this screen a heading is the way in — so the taxonomy reads as an invitation rather
        than as five boxes to learn.
      */}
      {complete && load.facts.length === 0 ? (
        <div className="memory__nothing">
          <h2 className="memory__nothing-title">Nothing lasting yet</h2>
          <p className="memory__nothing-body">
            This is genuinely empty, not hidden. Boone only keeps something when one of you says it
            matters — and asks first. Tell it something like{" "}
            <em>"we're trying to eat at home more"</em> or <em>"Sunday mornings stay free"</em> in
            your usual chat, and it'll show up here.
          </p>
        </div>
      ) : null}

      {CONTEXT_KIND_ORDER.map((kind) => {
        const all = (groups.known.get(kind) ?? []).filter((fact) => whoseMatches(fact, whose));
        const adding = form?.mode === "add" && form.kind === kind;
        /**
         * **A section holding an open form stays open**, whatever the fold says.
         *
         * Folding is a deliberate act, so it looks harmless — but a `FactForm` lives inside the
         * section, and hiding it unmounts it and takes whatever somebody had typed. That is the
         * same draft loss `refresh` goes to some length to avoid one function up, arriving
         * through a control nobody would think to check.
         */
        const holdsForm =
          adding || (form?.mode === "edit" && all.some((fact) => fact.ref === form.ref));
        const open = holdsForm || !folded.has(kind);
        const showingAll = expanded.has(kind);
        const shortened = all.slice(0, FOLD_AFTER);
        // The fold's other half of the same rule: *Show fewer* must not unmount the row
        // somebody is correcting either, which it would for anything past the third.
        const editedBelowTheFold =
          form?.mode === "edit" &&
          all.some((fact) => fact.ref === form.ref) &&
          !shortened.some((fact) => fact.ref === form.ref);
        const rows = showingAll || editedBelowTheFold ? all : shortened;
        // Split for the badge below: everything up to the last space, then the word the badge
        // has to stay with. A heading of one word is all tail.
        const heading = CONTEXT_KIND_HEADING[kind];
        const lastSpace = heading.lastIndexOf(" ");
        const headingLead = heading.slice(0, lastSpace + 1);
        const headingTail = heading.slice(lastSpace + 1);
        return (
          <section className="memory__section" key={kind}>
            <header className="memory__section-head">
              {/*
                **The fold control lives inside the heading, and the count inside the fold
                control.** A `<span>` styled like a heading would take the section out of a
                screen reader's heading list, which on a page of five sections is the whole of
                how it is navigated; a button wrapped in the `<h2>` is the standard disclosure
                shape.

                The count used to sit outside that button, to keep the heading's accessible
                name exactly the heading. That cost more than it bought, in both directions. On
                screen a sibling can only sit beside the heading's *box*, and a box ends where
                the line does, not where the sentence does — so at every width where a heading
                wrapped, the number drifted away from the words it counted, and no amount of
                styling reaches that, because the gap is the box. To anybody listening it was a
                bare "0" after the heading, tied to it by nothing but reading order. Inside, it
                is part of the name — *"What you've decided 0 saved"* — which is the sentence
                somebody actually needs; `saved` is what stops the number reading as the last
                word of the title.
              */}
              <h2 className="memory__heading">
                <button
                  className="memory__disclosure"
                  type="button"
                  aria-expanded={open}
                  onClick={() => setFolded((current) => toggle(current, kind))}
                >
                  <DisclosureIcon open={open} />
                  {headingLead}
                  {/*
                    **The last word and the badge travel as one unbreakable unit.** A badge is an
                    atomic inline box, so a browser filling a line with words and finding no room
                    left will put it on the next line *by itself* — the same drift this markup
                    exists to end, arriving one line lower. A non-breaking space does not stop it
                    (Chromium breaks between the space and the box anyway); holding the two
                    inside one `nowrap` span does, and costs a `lastIndexOf`.
                  */}
                  <span className="memory__heading-tail">
                    {headingTail}{" "}
                    {/* A count is a claim, so it is only made about a record we have all of. */}
                    {complete ? (
                      <span className="memory__count">
                        {/*
                          The number twice, and the duplication is the point: an accessible name
                          is built by joining the text of each child, and the join inserts
                          nothing — a hidden " saved" beside a visible "5" is announced as
                          "5saved", because the leading space is trimmed off the element that
                          carries it. One text node holds both, so the space is inside it and
                          survives; the visible digit is hidden from the name it would otherwise
                          double.
                        */}
                        <span aria-hidden="true">{all.length}</span>
                        <span className="visually-hidden">{`${all.length} saved`}</span>
                      </span>
                    ) : null}
                  </span>
                </button>
              </h2>
              {adding ? null : (
                <button
                  className="memory__add"
                  type="button"
                  data-focus-key={`add-${kind}`}
                  disabled={locked}
                  onClick={() => {
                    setRefusal(null);
                    // Adding into a folded section would open a form nobody can see.
                    setFolded((current) => {
                      const next = new Set(current);
                      next.delete(kind);
                      return next;
                    });
                    setForm({ mode: "add", kind });
                  }}
                >
                  <PlusIcon />
                  Add
                </button>
              )}
            </header>

            {open ? (
              <>
                {adding ? (
                  <FactForm
                    kind={kind}
                    members={load.members}
                    pending={pending}
                    locked={locked}
                    error={refusal?.at === "form" ? refusal.detail : null}
                    onSubmit={(values) => saveFact(kind, values)}
                    onCancel={() => closeForm(`add-${kind}`)}
                  />
                ) : null}

                {all.length === 0 ? (
                  /*
                    **Only a truncated read says anything here.** With the whole record loaded an
                    empty section needs no sentence — the heading and its *Add* say everything,
                    which is what `Memory.dc.html` draws — and a whole-record empty is answered
                    once, above, rather than five times. Past {@link MAX_ITEMS} the negative
                    would be a guess, so it is qualified instead.
                  */
                  complete ? null : (
                    <p className="memory__empty">
                      Nothing in what's loaded — there is more of your record than this page shows.
                    </p>
                  )
                ) : (
                  <ul className="memory__list">
                    {rows.map((fact) => (
                      <FactRow
                        key={fact.ref}
                        fact={fact}
                        kind={kind}
                        members={load.members}
                        editing={form?.mode === "edit" && form.ref === fact.ref}
                        pending={pending}
                        locked={locked}
                        setAside={isSetAside(fact, now)}
                        error={refusal?.at === "form" ? refusal.detail : null}
                        onEdit={() => {
                          setRefusal(null);
                          setForm({ mode: "edit", ref: fact.ref });
                        }}
                        onCancel={() => closeForm(`edit-${fact.ref}`)}
                        onSave={(values) => saveFact(kind, { ...values, existing: fact })}
                        onDelete={() => deleteFact(fact)}
                      />
                    ))}
                  </ul>
                )}

                {/* The fold, and the sentence that stops it reading as a limit. It is offered
                    only for a complete record, because "show all 12" said over an unknown
                    number is the claim this screen must not make. */}
                {complete && all.length > FOLD_AFTER ? (
                  <p className="memory__fold">
                    <button
                      className="linklike"
                      type="button"
                      onClick={() => setExpanded((current) => toggle(current, kind))}
                    >
                      {showingAll ? "Show fewer" : `Show all ${all.length}`}
                    </button>
                    {showingAll ? null : (
                      <span className="memory__fold-note">
                        {" "}
                        — nothing is held back; this section is just long
                      </span>
                    )}
                  </p>
                ) : null}
              </>
            ) : null}
          </section>
        );
      })}

      {groups.unknown.length === 0 ? null : (
        <section className="memory__section">
          <header className="memory__section-head">
            <h2 className="memory__heading">Also saved</h2>
          </header>
          <p className="memory__empty">
            This page is a little behind what Boone can store — these are yours, and you can still
            take any of them back.
          </p>
          <ul className="memory__list">
            {groups.unknown
              .filter((fact) => whoseMatches(fact, whose))
              .map((fact) => (
                <FactRow
                  key={fact.ref}
                  fact={fact}
                  kind={null}
                  members={load.members}
                  editing={false}
                  pending={pending}
                  locked={locked}
                  setAside={isSetAside(fact, now)}
                  error={null}
                  onEdit={null}
                  onCancel={() => closeForm(null)}
                  onSave={() => undefined}
                  onDelete={() => deleteFact(fact)}
                />
              ))}
          </ul>
        </section>
      )}

      {/* A filter that matches nothing says so, rather than leaving five silent sections that
          look like an empty record. It never says the record is empty — the chip above is what
          changed, and the sentence names it. */}
      {filtered && matching.length === 0 && load.facts.length > 0 ? (
        <p className="memory__empty">Nothing here is saved under that. Try "Everyone's".</p>
      ) : null}

      {/* The one stated limit. See {@link MAX_ITEMS}: it is a bound nobody has met, and the
          honest thing on the day somebody does is to say what is on screen and where the rest
          is, rather than to show a pager the rest of this screen is designed without. */}
      {complete ? null : (
        <div className="memory__truncated">
          <p>
            This is the first {load.facts.length.toLocaleString()} things Boone has saved about your
            family, and there are more. Ask <strong>@boone</strong> in Claude or ChatGPT for all of
            it.
          </p>
        </div>
      )}

      {confirming === null ? null : (
        <Confirm
          message={confirming.envelope.message}
          disclosures={doesNotReach(confirming.envelope)}
          pending={pending}
          error={refusal?.at === "dialog" ? refusal.detail : null}
          onConfirm={confirm}
          onCancel={() => {
            setConfirming(null);
            setRefusal(null);
          }}
        />
      )}
    </Screen>
  );
}

/** One saved fact, and the two things a family can do to it. */
function FactRow({
  fact,
  kind,
  members,
  editing,
  pending,
  locked,
  setAside,
  error,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: {
  fact: MemoryFact;
  /** `null` for a kind this build has no words for — it can be read and deleted, not edited. */
  kind: ContextKind | null;
  members: readonly RosterMember[];
  editing: boolean;
  pending: boolean;
  /** A write is in flight or a proposal is open — nothing here may start a second one. */
  locked: boolean;
  /** Archived, or past its expiry — computed once per render so the page agrees with itself. */
  setAside: boolean;
  error: string | null;
  onEdit: (() => void) | null;
  onCancel: () => void;
  onSave: (values: { title: string; content: string; subject: FactSubject }) => void;
  onDelete: () => void;
}) {
  const stale = staleNumber(fact);
  return (
    <li className="fact">
      <div className="fact__head">
        <h3 className="fact__title">{fact.title}</h3>
        {stale === null ? null : (
          <span className="fact__stale">name says {stale} — the fact below says otherwise</span>
        )}
      </div>
      <p className="fact__content">{fact.content}</p>
      {fact.rationale === null ? null : <p className="fact__why">Why: {fact.rationale}</p>}
      {/* Archived and expired are the two states in which a fact is on the record and no longer
          shaping plans (ADR 0003 decision 10 — a retrieval filter, never a deletion). Saying so
          is the difference between a family's record and a family's record as Boone reads it.
          This used to check `status` alone, so a lapsed-but-active fact rendered as current. */}
      {setAside ? (
        <p className="fact__note">Set aside — Boone no longer uses this when planning.</p>
      ) : null}
      <p className="fact__subject">{whose(fact)}</p>

      {editing && kind !== null ? (
        <FactForm
          kind={kind}
          fact={fact}
          members={members}
          pending={pending}
          locked={locked}
          error={error}
          onSubmit={onSave}
          onCancel={onCancel}
        />
      ) : (
        <div className="fact__actions">
          {onEdit === null ? null : (
            <button
              className="linklike"
              type="button"
              data-focus-key={`edit-${fact.ref}`}
              onClick={onEdit}
              disabled={locked}
            >
              Edit
            </button>
          )}
          <button
            className="linklike linklike--destructive"
            type="button"
            onClick={onDelete}
            disabled={locked}
          >
            Forget
          </button>
        </div>
      )}
    </li>
  );
}

const TITLE = "What Boone knows";
/**
 * How much of the record the walk will read before it stops and says so.
 *
 * A thousand durable facts — an order of magnitude past anything a family has, and the point is
 * that the bound *exists* rather than what it is. An unbounded loop over a paged read is one
 * slow morning away from a browser making a hundred requests for a screen nobody can read
 * anyway.
 *
 * **Counted in records rather than in pages** (PR #336 review, finding 5). This was
 * `MAX_PAGES = 20` under a comment claiming `MAX_PAGE_SIZE` was 50; it is 200, and
 * `DEFAULT_PAGE_SIZE` is the 50 the arithmetic actually relied on — which held only because
 * the walk sent no `?limit=`. Now that it asks for full pages, a page bound would be worse
 * still: the server returns a **short** page whenever DynamoDB stops at 1 MB, which for
 * `CONTEXT#` — content plus the family's own words — is a real possibility rather than a
 * theoretical one. A record count is exact whatever a page turns out to hold.
 */
const MAX_ITEMS = 1_000;

/** How many to ask for per request — `MAX_PAGE_SIZE`, the ceiling the contract allows. */
const PAGE_SIZE = 200;
/** How many rows a section shows before it offers to show the rest (`Memory.dc.html`). */
const FOLD_AFTER = 3;
const EMPTY: ReadonlySet<string> = new Set();

/**
 * The screen's opening paragraph — `Memory.dc.html`'s, with its claim made conditional.
 *
 * The canvas says *"sixteen things in all. That's the lot; nothing is held back"*, which is the
 * whole point of the sentence and is exactly what a partial read cannot say. So the count and
 * that clause travel together and appear only for a complete record; the rest — what this page
 * is for, and that deleting is real deletion — is true either way.
 */
function opening(total: number, complete: boolean): string {
  if (!complete) return OPENING;
  const count = total === 1 ? "one thing in all" : `${spell(total)} things in all`;
  return `Everything I've saved about your family — ${count}. That's the lot; nothing is held back. ${CORRECT_OR_DELETE}`;
}

const CORRECT_OR_DELETE =
  "If anything is wrong, out of date, or was never quite what you meant, put it right here. If you'd rather I didn't have it at all, delete it — properly, not just hidden.";
/** The same paragraph with no count and no completeness claim — loading, failed, or truncated. */
const OPENING = `Everything I've saved about your family. ${CORRECT_OR_DELETE}`;

const GENERIC_READ_FAILURE =
  "Something went wrong at our end. Try again in a moment — nothing you've told Boone is affected.";
const GENERIC_WRITE_FAILURE =
  "Something went wrong at our end, and nothing was changed. Try again in a moment.";
/**
 * What to say when we genuinely do not know whether the write landed.
 *
 * **`appFetch` rejects for two different things**: a refusal the server sent, and a request
 * whose response never came back. The second covers a write that committed — a one-turn
 * `remember`, or a confirmed `forget_this` — and telling that family *"nothing was changed"*
 * is a confident falsehood about an irreversible operation. Retrying does not recover the
 * answer either, because each attempt mints a fresh `idempotencyKey`, so the client cannot
 * tell a replay from a second write.
 *
 * So the copy claims only what is true, and the screen re-reads rather than leaving them to
 * guess. It is the same posture `forget_this`'s own retryable copy takes for a lost response.
 */
const AMBIGUOUS_WRITE_FAILURE =
  "We couldn't tell whether that went through, so nothing here is certain yet. Check your record before trying again.";
/**
 * What replaces the sentence above once the re-read has failed too (PR #336 review, finding 4).
 *
 * The version before this one said *"your record has been re-read below"* the instant the write
 * failed — a claim about what is on screen, made before the read that would make it true, and
 * one the very same outage takes down as well. Somebody deciding whether to retry an
 * irreversible delete was doing it on the strength of rows that had never been refreshed.
 */
const AMBIGUOUS_AND_UNREAD =
  "We couldn't tell whether that went through, and we couldn't re-read your record either. Give it a moment and check the list before trying again.";

type Load =
  | { status: "loading" }
  | { status: "failed"; detail: string | null }
  | {
      status: "ready";
      facts: readonly MemoryFact[];
      /** The walk hit {@link MAX_ITEMS} — every count and completeness claim is off. */
      truncated: boolean;
      members: readonly RosterMember[];
    };

/**
 * Read the family's whole durable record, following the cursor to the end.
 *
 * Bounded by {@link MAX_ITEMS} and honest about it: `truncated` is what every completeness
 * claim on the screen hangs off. It is a plain loop rather than anything cleverer because the
 * pages are strictly ordered and each cursor comes from the answer before it.
 */
async function readWholeRecord(
  api: AppApi,
  familyId: string,
): Promise<{ facts: MemoryFact[]; truncated: boolean }> {
  const facts: MemoryFact[] = [];
  let cursor: string | null = null;
  do {
    const page = await fetchMemoryPage(api, familyId, cursor, PAGE_SIZE);
    facts.push(...page.records);
    cursor = page.nextCursor;
  } while (cursor !== null && facts.length < MAX_ITEMS);
  return { facts, truncated: cursor !== null };
}

/**
 * Where a refusal belongs on the screen.
 *
 * **Stated by whoever sent the write, never inferred from what happens to be open.** One
 * string shared by three renderers, shown wherever "no form and no dialog" happened to be
 * true, put a delete's refusal *inside an unrelated open add-a-goal form* — attached to a
 * write the family did not make, under a heading it had nothing to do with. `send` is called
 * by the code that knows which it is, so it is a parameter.
 */
type RefusalAt = "form" | "dialog" | "page";

interface Refusal {
  readonly detail: string;
  readonly at: RefusalAt;
}

type FormState = { mode: "add"; kind: ContextKind } | { mode: "edit"; ref: string };

interface Confirming {
  envelope: WriteEnvelope;
  tail: "/remember" | "/correct-this" | "/forget-this";
  proposalId: string;
  /** Whether applying this should clear the filter — see `send`'s parameter of the same name. */
  reveal: boolean;
}

/**
 * The server's `doesNotReach` lines, when the envelope carries them.
 *
 * Narrowed at the one place the panel meets it rather than typed into {@link Confirming},
 * because only `forget_this` has them — and a `Confirm` that took a union of envelopes would
 * make the dialog know which tool it is confirming, which is the one thing it must not.
 */
function doesNotReach(envelope: WriteEnvelope): readonly string[] {
  const { doesNotReach: lines } = envelope as Partial<ForgetEnvelope>;
  return Array.isArray(lines) ? lines : [];
}

/**
 * Whether Boone has stopped consulting this fact.
 *
 * **Two states, not one.** Retrieval treats a fact as live iff `status == "active"` *and*
 * `expiresAt` is absent or in the future (`queryActiveContext`); `GET /memory` drops that
 * filter on purpose and discloses `expiresAt` precisely so this screen can tell. Checking only
 * `status` renders a lapsed fact as a current one, which is the "presents uncertain memories
 * as facts" failure the disclosure exists to prevent.
 */
function isSetAside(fact: MemoryFact, now: number): boolean {
  if (fact.status !== "active") return true;
  if (fact.expiresAt === null) return false;
  const at = Date.parse(fact.expiresAt);
  return Number.isFinite(at) && at <= now;
}

/**
 * Whose a fact is, in the family's words — three cases, because #92 says three (never two).
 *
 * Lowercase and inline since #329: `Memory.dc.html` reads it as part of the row (*the
 * family's*, *Dana's*, *whose this is — not recorded*) rather than as the shouting uppercase
 * pill #302 shipped. It is a note about the fact, not a status badge on it.
 */
function whose(fact: MemoryFact): string {
  if (fact.subject.kind === "family") return "the family's";
  if (fact.subject.kind === "member") {
    return fact.subject.name === null ? "someone who has left" : `${fact.subject.name}'s`;
  }
  return "whose this is — not recorded";
}

/**
 * A number in the title that the fact underneath contradicts — `Memory.dc.html`'s annotation
 * on the `Budget ~$350k` row, which is #265 drawn as a state (#329).
 *
 * **The rule is one sentence and it has no threshold in it**, which matters because #265
 * itself declined a server-side version of this on exactly that ground: *"every hedge drafted
 * for the rule failed its own test"*. The canvas's own words are *"the fact below says
 * otherwise"*, and something can only say otherwise if it says something — so the check is:
 *
 * > the title carries a number, the content carries a number, and none of the title's numbers
 * > appears in the content.
 *
 * That third clause is what makes it safe rather than a guess. #265's narrow exception is a
 * number that **names** rather than measures — *"Year 6 school run"* — and its content
 * ordinarily carries no number at all, so nothing contradicts it and nothing is flagged.
 * *"Three-hour drive limit"* and *"Two children and a dog"* are not caught either: their
 * numbers are words, and only digits are compared.
 *
 * **The residual is real and small**: *"Year 6 school run"* whose content happens to say
 * *"starts at 8:30"* is flagged wrongly. It costs a family one sentence suggesting they look
 * at a fact of theirs, which is a good deal cheaper than the failure it catches — a title
 * carrying a retracted amount, which is the **only** thing `recall`'s default projection
 * hands a host (ADR 0012 decision 3), so it is the version of the fact Boone plans with.
 *
 * There is no fix on this screen and that is deliberate: renaming is #306, and until it lands
 * the recovery is the one {@link FactForm} already names — add it under the right name, delete
 * this one. Saying nothing would leave the family unable to see the problem at all.
 */
function staleNumber(fact: MemoryFact): string | null {
  const inTitle = numbers(fact.title);
  if (inTitle.length === 0) return null;
  const inContent = numbers(fact.content);
  if (inContent.length === 0) return null;
  const contradicted = inTitle.filter((one) => !inContent.includes(one));
  return contradicted.length === inTitle.length ? (contradicted[0] ?? null) : null;
}

/**
 * The digit-bearing tokens in a string, normalised for comparison.
 *
 * Grouping separators and a leading currency symbol are dropped — `$350k` and `350k` are the
 * same number said twice — and everything is lowercased so `350K` matches `350k`. Words are
 * not read as numbers, which is the exception in {@link staleNumber} doing its job.
 */
function numbers(text: string): string[] {
  const found = text.toLowerCase().match(/\d[\d,.]*\s?[km%]?/g) ?? [];
  return [...new Set(found.map((one) => one.replace(/[\s,]/g, "").replace(/\.$/, "")))];
}

/** The five known kinds in their own buckets, and anything else kept aside rather than dropped. */
function groupByKind(facts: readonly MemoryFact[]): {
  known: Map<ContextKind, MemoryFact[]>;
  unknown: MemoryFact[];
} {
  const known = new Map<ContextKind, MemoryFact[]>();
  const unknown: MemoryFact[] = [];
  for (const fact of facts) {
    if (isContextKind(fact.kind)) {
      const bucket = known.get(fact.kind);
      if (bucket === undefined) known.set(fact.kind, [fact]);
      else bucket.push(fact);
    } else {
      unknown.push(fact);
    }
  }
  return { known, unknown };
}

/**
 * The server's sentence for a refusal, or `null` to fall back to a generic one.
 *
 * **`invalid_input` is deliberately dropped**, and it is the one code that must be. #298
 * answers a schema failure with `z.prettifyError`, which is developer-facing by design — it
 * names a field's path — and every other refusal it relays is the tool's own family-facing
 * words. A family reading *"✖ Invalid option: expected one of GOAL|CONSTRAINT|…"* would be
 * shown the taxonomy this screen exists to hide, over a body **the panel built**: a schema
 * failure here is our bug and not something they did, so the generic line is also the true one.
 */
function detailOf(error: unknown): string | null {
  if (!(error instanceof AppRequestError)) return null;
  return error.code === "invalid_input" ? null : error.detail;
}

/**
 * Did the server answer at all?
 *
 * The one question two decisions turn on — what the family is told, and whether the record is
 * re-read — so it is asked once. `status === 0` is `appFetch`'s "nothing answered", and a throw
 * that is not an `AppRequestError` never reached the check at all.
 */
function wasAnswered(error: unknown): boolean {
  return error instanceof AppRequestError && error.status > 0;
}
